import { localizedStrings } from '@core/strings'
import { Entity } from '@core/entities/entity'
import { IAuditResult } from './IAuditResult'
import {
  AuditAction,
  AuditResultPlatform,
} from '@core/entities/audit/AuditResult/types'
import { User } from '@core/entities/user'
import { plainToInstance, Type } from 'class-transformer'
import { RegisterItemWithLinksPropertyData } from '@core/entities/register/RegisterItem'
import moment from 'moment'
import { Feature, FeatureCollection, GeoJSON, Point } from 'geojson'
import circle from '@turf/circle'
import type { Polygon, Units } from '@turf/helpers'
import { AuthGroup } from '@core/entities/Groups/AuthGroups'
import { AuditResultMetadata } from '@core/entities/audit/AuditResult/AuditResultMetadata'
import { AuditDraftResult } from '@core/entities/audit/AuditResult/AuditDraftResult'

export class AuditResult extends Entity<IAuditResult> {
  id: number

  action: AuditAction

  platform: AuditResultPlatform

  signatureUrl?: string

  info?: string

  createdOn?: string

  @Type(() => User)
  user?: User

  @Type(() => User)
  organisationUser?: User

  @Type(() => User)
  appliedToUser?: User

  @Type(() => User)
  projectUser?: User

  @Type(() => AuditResultMetadata)
  metadata: AuditResultMetadata

  geoJSON?: GeoJSON

  project?: {
    id: number
    name: string
  }

  organisation?: {
    id: number
    name: string
  }

  permit?: {
    id: number
    shortUUID: string
    templateName: string
  }

  checklist?: {
    id: number
    shortUUID: string
    templateName: string
  }

  register?: {
    id: number
    name: string
  }

  registerItem?: {
    id: number
    uniqueId: string
  }

  permitTemplate?: {
    id: number
    name: string
  }

  permitTemplateVersion?: {
    id: number
    version: number
  }

  checklistTemplate?: {
    id: number
    name: string
  }

  checklistTemplateVersion?: {
    id: number
    version: number
  }

  @Type(() => AuthGroup)
  group?: AuthGroup

  siteboxDocument?: {
    id: number
    name: string
  }

  siteboxDocumentVersion?: {
    id: number
    name: string
    documentVersion: number
  }

  @Type(() => AuthGroup)
  appliedToGroups?: Array<AuthGroup>

  @Type(() => AuditDraftResult)
  permitDraft?: AuditDraftResult

  @Type(() => AuditDraftResult)
  checklistDraft?: AuditDraftResult

  public static new(payload: unknown): AuditResult {
    const entity = plainToInstance(AuditResult, payload)

    return entity
  }

  public isAction(action: AuditAction): boolean {
    return this.action === action
  }

  public getPlatformText = () => {
    switch (this.platform) {
      case 'WEB':
        return localizedStrings.webBrowser
      case 'IOS':
        return localizedStrings.ios
      case 'ANDROID':
        return localizedStrings.android
      // The UNKNOWN platform exists because historically we didn't gather the type of device the person was using
      case 'UNKNOWN':
      default:
        return ''
    }
  }

  public getActionLabel(): string {
    switch (this.action) {
      case 'PERMIT_APPROVED':
        return localizedStrings.approved
      case 'PERMIT_RECEIVED':
        return localizedStrings.permitReceived
      case 'PERMIT_REQUESTED':
        return localizedStrings.requested
      case 'CHECKLIST_COMPLETED':
        return localizedStrings.initiated
      case 'CHECKLIST_SUBMITTED_FOR_APPROVAL':
        return localizedStrings.requested
      case 'CHECKLIST_APPROVED':
        return localizedStrings.approved
      case 'CHECKLIST_REJECTED':
        return localizedStrings.rejected
      case 'CHECKLIST_CHANGES_REQUESTED':
        return localizedStrings.changesRequested
      case 'CHECKLIST_CHANGES_ADDRESSED':
        return localizedStrings.changesAddressed
      case 'CHECKLIST_APPROVAL_NOMINATION':
        return localizedStrings.approvalNomination
      case 'PERMIT_OPENED':
        return localizedStrings.opened
      case 'PERMIT_POST_PERMIT_CHECK_COMPLETE':
        return localizedStrings.postPermitChecksComplete
      case 'PERMIT_SIGN_OFF_COMPLETE':
        return localizedStrings.finalSignOff
      case 'WORK_COMPLETE':
        return localizedStrings.permitClosed
      case 'PERMIT_TRANSFER_CANCELLED':
        return localizedStrings.transferCanceled
      case 'PERMIT_APPROVAL_NOMINATION':
        return localizedStrings.approvalNomination
      case 'PERMIT_SIGNED':
        return localizedStrings.permitSigned
      case 'PERMIT_CHANGES_ADDRESSED':
        return localizedStrings.changesAddressed
      case 'PERMIT_PERIODIC_CHECKS_SUBMITTED':
        return localizedStrings.periodicCheckSubmitted
      case 'PERMIT_CHECKIN':
        return localizedStrings.locationCheckIn
      case 'PERMIT_CHECKED_IN':
        return localizedStrings.checkedIn
      case 'PERMIT_CHECKED_OUT':
        return localizedStrings.checkedOut
      case 'PERMIT_CHECK_IN_EXTENDED':
        return localizedStrings.checkInExtended
      case 'PERMIT_FORCE_CHECKED_OUT':
        return localizedStrings.forceCheckedOut
      case 'PERMIT_DRAFT_DELETED':
        return localizedStrings.permitDraftDeleted
      case 'CHECKLIST_DRAFT_DELETED':
        return localizedStrings.checklistDraftDeleted
      case 'PERMIT_DRAFT_CREATED':
        return localizedStrings.permitDraftCreated
      case 'CHECKLIST_DRAFT_CREATED':
        return localizedStrings.checklistDraftCreated
      case 'PARTICIPANT_REMOVED':
        return localizedStrings.participantRemoved
      case 'PERMIT_CHECKLIST_DONE':
        return localizedStrings.checklistCompleted
      case 'BRIEFING_SESSION_DONE':
        return localizedStrings.participantsBriefed
      case 'BRIEFING_SESSION_NO_SIGN_PARTICIPANTS_ADDED':
        return localizedStrings.participantsAdded
      case 'PARTICIPANT_BRIEFED':
        return this.metadata?.selfBriefed
          ? localizedStrings.participantBriefedViaQRCode
          : localizedStrings.participantBriefed
      case 'BRIEFING_ACCEPTED':
        return localizedStrings.remoteBriefingAccepted
      case 'BRIEFING_DECLINED':
        return localizedStrings.remoteBriefingDeclined
      case 'PERMIT_EXTENSION_APPROVED':
        return localizedStrings.extensionApproved
      case 'PERMIT_TRANSFER_ACCEPTED':
        return localizedStrings.transferAccepted
      case 'PERMIT_TRANSFER_APPROVED':
        return localizedStrings.permitTransferApproved
      case 'PERMIT_TRANSFER_DECLINED':
        return localizedStrings.permitTransferDeclined
      case 'SENT_TO_PERMIT_HOLDER':
        return localizedStrings.sentToPermitHolder
      case 'PERMIT_EXTENSION_REQUESTED':
        return localizedStrings.extensionsRequested
      case 'PERMIT_CHANGES_REQUESTED':
        return localizedStrings.changesRequested
      case 'PERMIT_TRANSFER_INITIATED':
        return localizedStrings.transferInitiated
      case 'PERMIT_HOLDER_CHANGED':
        return localizedStrings.permitHolderChanged
      case 'PERMIT_REJECTED':
        return localizedStrings.permitRejected
      case 'PERMIT_EXTENSION_REJECTED':
        return localizedStrings.extensionRejected
      case 'PERMIT_TRANSFER_REJECTED':
        return localizedStrings.transferRejected
      case 'PERMIT_CANCELLED':
        return localizedStrings.cancelled
      case 'PERMIT_SUSPENDED':
        return localizedStrings.suspended
      case 'PERMIT_SUSPENDED_RESUME':
        return localizedStrings.resumed
      case 'PERMIT_SUSPENDED_RESUME_REQUESTED':
        return localizedStrings.resumeRequested
      case 'PERMIT_SUSPENSION_RESUME_REQUEST_DECLINED':
        return localizedStrings.resumeRequestRejected
      case 'PERMIT_SUSPENSION_RESUME_REQUEST_APPROVED':
        return localizedStrings.resumeRequestApproved
      case 'PERMIT_SHORTENED':
        return localizedStrings.shortened
      case 'PERMIT_DISCARDED':
        return localizedStrings.discarded
      case 'PERMIT_EXPIRED':
        return localizedStrings.expired
      case 'TASK_CANCELLED':
        return localizedStrings.cancelled
      case 'PROJECT_EXTERNAL_CODE_CHANGED':
        return localizedStrings.changedProjectExternalCode
      case 'PROJECT_STATUS_CHANGED':
        return localizedStrings.changedProjectStatus
      case 'PROJECT_MEMBER_ADDED':
        return localizedStrings.addedUserToProject
      case 'PROJECT_MEMBER_REMOVED':
        return localizedStrings.removedUserFromProject
      case 'PROJECT_MEMBER_ACTIVATED':
        return localizedStrings.activatedProjectMember
      case 'PROJECT_MEMBER_DISABLED':
        return localizedStrings.disabledProjectMember
      case 'PROJECT_MEMBER_ROLE_UPDATED':
        return localizedStrings.updatedProjectPermissions
      case 'USER_ADDED_TO_ORGANISATION':
        return localizedStrings.addedUserToOrganisation
      case 'USER_PROFILE_CHANGED':
        return localizedStrings.changedProfileInformation
      case 'USER_ROLE_UPDATED_IN_ORGANISATION':
        return localizedStrings.updatedOrganisationPermissions
      case 'ORGANISATION_MEMBER_ACTIVATED':
        return localizedStrings.activatedOrganisationMember
      case 'ORGANISATION_MEMBER_DISABLED':
        return localizedStrings.disabledOrganisationMember
      case 'CHECKLIST_TEMPLATE_VERSION_ACTIVATED':
        return localizedStrings.activatedChecklistVersion
      case 'CHECKLIST_TEMPLATE_VERSION_CREATED':
        return localizedStrings.createdChecklistVersion
      case 'CHECKLIST_TEMPLATE_VERSION_UPDATED':
        return localizedStrings.updatedChecklistVersion
      case 'CHECKLIST_TEMPLATE_VERSION_DISCARDED':
        return localizedStrings.discardedChecklistVersion
      case 'CHECKLIST_TEMPLATE_VERSION_MOVED_TO_DRAFT':
        return localizedStrings.movedChecklistVersionToDraft
      case 'CHECKLIST_TEMPLATE_CREATED':
        return localizedStrings.createdChecklistTemplate
      case 'CHECKLIST_TEMPLATE_ARCHIVED':
        return localizedStrings.archivedChecklistTemplate
      case 'CHECKLIST_TEMPLATE_UNARCHIVED':
        return localizedStrings.unarchivedChecklistTemplate
      case 'CHECKLIST_TEMPLATE_UPDATED':
        return localizedStrings.updatedChecklistTemplate
      case 'CHECKLIST_TEMPLATE_MOVED':
        return localizedStrings.movedChecklistTemplate
      case 'TEMPLATE_VERSION_CREATED':
        return localizedStrings.createdPermitTemplateVersion
      case 'TEMPLATE_VERSION_ACTIVATED':
        return localizedStrings.activatedPermitTemplateVersion
      case 'TEMPLATE_VERSION_UPDATED':
        return localizedStrings.updatedPermitTemplateVersion
      case 'TEMPLATE_VERSION_DELETED':
        return localizedStrings.deletedPermitTemplateVersion
      case 'TEMPLATE_VERSION_MOVED_TO_DRAFT':
        return localizedStrings.movedPermitTemplateVersionToDraft
      case 'TEMPLATE_CREATED':
        return localizedStrings.createdPermitTemplate
      case 'TEMPLATE_UPDATED':
        return localizedStrings.updatedPermitTemplate
      case 'TEMPLATE_PUBLISHED':
        return localizedStrings.publishedPermitTemplate
      case 'TEMPLATE_MOVED':
        return localizedStrings.movedPermitTemplate
      case 'TEMPLATE_UNARCHIVED':
        return localizedStrings.unarchivedPermitTemplate
      case 'TEMPLATE_ARCHIVED':
        return localizedStrings.archivedPermitTemplate
      case 'TEMPLATE_UNPUBLISHED':
        return localizedStrings.templateUnpublished
      case 'USER_ADDED_TO_ORGANISATION_AUTH_GROUP':
      case 'USER_ADDED_TO_PROJECT_AUTH_GROUP':
        return localizedStrings.addedUserToAuthGroup
      case 'USER_REMOVED_FROM_ORGANISATION_AUTH_GROUP':
      case 'USER_REMOVED_FROM_PROJECT_AUTH_GROUP':
        return localizedStrings.removedUserFromAuthGroup
      case 'PROJECT_PERMISSION_ASSIGNMENTS_UPDATED':
        return localizedStrings.updatedAuthGroupPermissions
      case 'PROJECT_TEMPLATE_ADDED':
        return localizedStrings.addedPermitTemplate
      case 'PROJECT_TEMPLATE_REMOVED':
        return localizedStrings.removedPermitTemplate
      case 'PROJECT_TEMPLATE_VERSION_UPDATED':
        return localizedStrings.updatedProjectPermitSettings
      case 'PROJECT_APPROVAL_ASSIGNMENTS_UPDATED':
        return localizedStrings.updatedPermitApprovalSettings
      case 'PROJECT_CHECKLIST_TEMPLATE_VERSION_UPDATED':
        return localizedStrings.updatedProjectChecklistVersionSettings
      case 'PROJECT_CHECKLIST_TEMPLATE_REMOVED':
        return localizedStrings.removedChecklistTemplateFromProject
      case 'PROJECT_CHECKLIST_TEMPLATE_ADDED':
        return localizedStrings.addedChecklistTemplateToProject
      case 'PROJECT_UPDATED':
        return localizedStrings.projectUpdated
      case 'COMMENT_CREATED':
        return localizedStrings.newComment
      case 'IMAGE_WARNINGS_CREATED':
        return localizedStrings.warningDetected
      case 'AUTH_GROUP_CREATED':
        return localizedStrings.authGroupCreated
      case 'CHECKLIST_TEMPLATE_GROUP_PERMISSIONS_ASSIGNMENTS_UPDATED':
        return localizedStrings.authGroupChecklistPermissionsUpdated
      case 'PERMIT_TEMPLATE_GROUP_PERMISSIONS_ASSIGNMENTS_UPDATED':
        return localizedStrings.authGroupPermitPermissionsUpdated
      case 'PERMIT_TEMPLATE_ALLOWED_CHILDREN_UPDATED':
        return localizedStrings.permitTemplateAllowedChildrenUpdated
      case 'PERMIT_PARENT_LINKED':
        return localizedStrings.permitLinkedToParent
      case 'PERMIT_CLOSURE_BLOCKED_BY_CHILDREN':
        return localizedStrings.permitClosureBlockedByChildren
      case 'REGISTER_GROUP_PERMISSIONS_ASSIGNMENTS_UPDATED':
        return localizedStrings.authGroupRegisterPermissionsUpdated
      case 'AUTH_GROUP_NAME_UPDATED':
        return localizedStrings.authGroupNameUpdated
      case 'AUTH_GROUP_DELETED':
        return localizedStrings.authGroupDeleted
      case 'FOLDER_MOVED':
        return localizedStrings.folderMoved
      case 'FOLDER_RENAMED':
        return localizedStrings.folderRenamed
      case 'FOLDER_CREATED':
        return localizedStrings.folderCreated
      case 'SITE_BOX_DOCUMENT_VERSION_STATUS_CHANGED':
        return localizedStrings.siteBoxDocumentVersionStatusChanged
      case 'SITE_BOX_DOCUMENT_VERSION_CREATED':
        return localizedStrings.siteBoxDocumentVersionCreated
      case 'SITE_BOX_DOCUMENT_MOVED':
        return localizedStrings.siteBoxDocumentMoved
      case 'SITE_BOX_DOCUMENT_STATUS_CHANGED':
        return localizedStrings.siteBoxDocumentStatusChanged
      case 'SITE_BOX_DOCUMENT_UPDATED':
        return localizedStrings.siteBoxDocumentUpdated
      case 'SITE_BOX_DOCUMENT_CREATED':
        return localizedStrings.siteBoxDocumentCreated
      case 'ORGANISATION_PROJECT_ADDED':
        return localizedStrings.projectCreated
      case 'GROUP_PERMISSIONS_ASSIGNMENTS_UPDATED':
        return localizedStrings.updatedOrgAuthGroupPermissions
      case 'TASK_CREATED':
        return localizedStrings.created
      case 'TASK_UPDATED':
        return localizedStrings.updated
      case 'TASK_WORK_STEP_SUBMITTED':
        return localizedStrings.moved
      case 'TASK_REASSIGNED':
        return localizedStrings.reassignTask
      case 'TASK_CHANGES_REQUESTED':
        return localizedStrings.changesRequested
      case 'TASK_APPROVED':
        return localizedStrings.approved
      case 'TASK_REOPENED':
        return localizedStrings.reopenTask
      // ARCHIVED is the legacy value
      case 'ARCHIVED':
      case 'REGISTER_ITEM_ARCHIVED':
        return localizedStrings.itemArchived
      // CREATED is the legacy value
      case 'CREATED':
      case 'REGISTER_ITEM_CREATED':
        return this.metadata?.inputType === 'CSV_UPLOAD'
          ? localizedStrings.itemCreatedViaCsv
          : localizedStrings.itemCreated
      // UPDATED is the legacy value
      case 'UPDATED':
      case 'REGISTER_ITEM_UPDATED':
        return this.metadata?.inputType === 'CSV_UPLOAD'
          ? localizedStrings.itemUpdatedViaCsv
          : localizedStrings.itemUpdated
      // UNARCHIVED is the legacy value
      case 'UNARCHIVED':
      case 'REGISTER_ITEM_UNARCHIVED':
        return localizedStrings.itemUnarchived
      case 'REGISTER_ITEM_EMAIL_NOTIFICATION_SENT':
        return localizedStrings.registerItemEmailNotificationSent
      case 'REGISTER_ITEM_FORMULAS_RECALCULATED':
        return localizedStrings.formulasRecalculated
      // INSPECTION_EXPIRED is the legacy value
      case 'INSPECTION_EXPIRED':
      case 'REGISTER_ITEM_INSPECTION_EXPIRED':
        return localizedStrings.inspectionExpired
      // INSPECTION_SUBMITTED is the legacy value
      case 'INSPECTION_SUBMITTED':
      case 'REGISTER_ITEM_INSPECTION_SUBMITTED':
        return localizedStrings.inspectionDone
      case 'REGISTER_ITEM_MOVED_TO_DIAGRAM':
        return localizedStrings.itemTransferredToDiagram
      case 'REGISTER_ITEM_LINKED_TO_PERMIT':
        return localizedStrings.itemLinkedToPermit
      case 'PERMIT_EXTENSION_CANCELLED':
        return localizedStrings.extensionRequestCancelled
      case 'ORGANISATION_COMPANY_CREATED':
        return localizedStrings.newCompanyAdded
      case 'ORGANISATION_COMPANY_UPDATED':
        return localizedStrings.companyEdited
      case 'ORGANISATION_COMPANY_MERGED':
        return localizedStrings.companyMerge
      case 'ORGANISATION_COMPANY_DELETED':
        return localizedStrings.companyDeleted
      case 'COMPANY_ONBOARDING_SUBMITTED':
        return localizedStrings.companyOnboardingSubmitted
      case 'COMPANY_ONBOARDING_APPROVED':
        return localizedStrings.companyOnboardingApproved
      case 'COMPANY_ONBOARDING_CHANGES_REQUESTED':
        return localizedStrings.companyOnboardingChangesRequested
      case 'COMPANY_ONBOARDING_RESUBMITTED':
        return localizedStrings.companyOnboardingResubmitted
      case 'COMPANY_ONBOARDING_REJECTED':
        return localizedStrings.companyOnboardingRejected
      case 'COMPANY_ONBOARDING_COMPANY_ADDED_TO_PROJECT':
        return this.project?.name
          ? localizedStrings.companyOnboardingCompanyAddedToNamedProject(
              this.project.name,
            )
          : localizedStrings.companyOnboardingCompanyAddedToProject
      case 'COMPANY_ONBOARDING_USER_ASSIGNED':
        return this.metadata?.orgRole === 'COMPANY_ADMIN'
          ? localizedStrings.companyOnboardingAdminAssigned
          : localizedStrings.companyOnboardingUserAssigned
      case 'COMPANY_COMPLIANCE_DOCUMENT_UPLOADED':
        return this.metadata?.complianceStatusBefore === 'NOT_UPLOADED'
          ? localizedStrings.companyComplianceRequirementUploaded
          : localizedStrings.companyComplianceRequirementReuploaded
      case 'COMPANY_COMPLIANCE_DOCUMENT_APPROVED':
        return localizedStrings.companyComplianceRequirementApproved
      case 'COMPANY_COMPLIANCE_DOCUMENT_REJECTED':
        return localizedStrings.companyComplianceRequirementRejected
      case 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRED':
        return localizedStrings.companyComplianceRequirementExpired
      case 'COMPANY_COMPLIANCE_UNASSIGNED':
        return localizedStrings.companyComplianceRequirementUnassigned
      case 'COMPANY_COMPLIANCE_COMPANY_ACTIVATED':
        return localizedStrings.companyComplianceCompanyActivated
      case 'REGISTER_ITEM_LINKED_TO_CHECKLIST':
        return localizedStrings.itemLinkedToChecklist
      case 'ORGANISATION_FEATURE_FLAG_UPDATED':
        return localizedStrings.organisationFeatureFlagUpdated
      case 'USER_COMPETENCY_COMPLETED':
        return localizedStrings.userCompetencyCompleted
      case 'USER_COMPETENCY_EXPIRED':
        return localizedStrings.userCompetencyExpired
      default:
        return this.action
    }
  }

  public getActionColor(): string {
    switch (this.action) {
      case 'CHECKLIST_COMPLETED':
      case 'PERMIT_POST_PERMIT_CHECK_COMPLETE':
      case 'PERMIT_SIGN_OFF_COMPLETE':
      case 'WORK_COMPLETE':
      case 'PERMIT_SIGNED':
      case 'PERMIT_PERIODIC_CHECKS_SUBMITTED':
      case 'PERMIT_CHECKIN':
      case 'PERMIT_DRAFT_CREATED':
      case 'CHECKLIST_DRAFT_CREATED':
      case 'PERMIT_CHECKLIST_DONE':
      case 'PERMIT_SUSPENDED_RESUME_REQUESTED':
      case 'UNARCHIVED':
      case 'INSPECTION_SUBMITTED':
      case 'BRIEFING_SESSION_DONE':
      case 'BRIEFING_SESSION_NO_SIGN_PARTICIPANTS_ADDED':
      case 'PARTICIPANT_BRIEFED':
      case 'ORGANISATION_COMPANY_CREATED':
      case 'ORGANISATION_COMPANY_UPDATED':
      case 'ORGANISATION_COMPANY_MERGED':
      case 'COMMENT_CREATED':
      case 'COMPANY_ONBOARDING_COMPANY_ADDED_TO_PROJECT':
      case 'COMPANY_ONBOARDING_USER_ASSIGNED':
      case 'COMPANY_COMPLIANCE_DOCUMENT_UPLOADED':
      case 'PERMIT_CHECKED_OUT':
        return 'primary'

      case 'PERMIT_OPENED':
      case 'PERMIT_EXTENSION_APPROVED':
      case 'PERMIT_TRANSFER_ACCEPTED':
      case 'PERMIT_TRANSFER_APPROVED':
      case 'PERMIT_CHANGES_ADDRESSED':
      case 'PERMIT_APPROVED':
      case 'CHECKLIST_APPROVED':
      case 'CHECKLIST_CHANGES_ADDRESSED':
      case 'PERMIT_RECEIVED':
      case 'PERMIT_SUSPENDED_RESUME':
      case 'PERMIT_SUSPENSION_RESUME_REQUEST_APPROVED':
      case 'CREATED':
      case 'UPDATED':
      case 'TEMPLATE_PUBLISHED':
      case 'PROJECT_TEMPLATE_ADDED':
      case 'TEMPLATE_VERSION_CREATED':
      case 'TEMPLATE_VERSION_ACTIVATED':
      case 'CHECKLIST_TEMPLATE_CREATED':
      case 'CHECKLIST_TEMPLATE_VERSION_CREATED':
      case 'CHECKLIST_TEMPLATE_VERSION_ACTIVATED':
      case 'TASK_CREATED':
      case 'FOLDER_CREATED':
      case 'SITE_BOX_DOCUMENT_VERSION_CREATED':
      case 'SITE_BOX_DOCUMENT_CREATED':
      case 'ORGANISATION_PROJECT_ADDED':
      case 'COMPANY_ONBOARDING_APPROVED':
      case 'COMPANY_COMPLIANCE_DOCUMENT_APPROVED':
      case 'COMPANY_COMPLIANCE_COMPANY_ACTIVATED':
      case 'USER_COMPETENCY_COMPLETED':
      case 'BRIEFING_ACCEPTED':
      case 'PERMIT_CHECKED_IN':
      case 'PERMIT_CHECK_IN_EXTENDED':
        return 'success'

      case 'PERMIT_REQUESTED':
      case 'CHECKLIST_SUBMITTED_FOR_APPROVAL':
      case 'CHECKLIST_CHANGES_REQUESTED':
      case 'SENT_TO_PERMIT_HOLDER':
      case 'PERMIT_EXTENSION_REQUESTED':
      case 'PERMIT_CHANGES_REQUESTED':
      case 'PERMIT_TRANSFER_INITIATED':
      case 'PERMIT_HOLDER_CHANGED':
      case 'TASK_CHANGES_REQUESTED':
      case 'IMAGE_WARNINGS_CREATED':
      case 'TEMPLATE_ARCHIVED':
      case 'TEMPLATE_UNPUBLISHED':
      case 'TEMPLATE_VERSION_MOVED_TO_DRAFT':
      case 'CHECKLIST_TEMPLATE_ARCHIVED':
      case 'CHECKLIST_TEMPLATE_VERSION_MOVED_TO_DRAFT':
      case 'COMPANY_ONBOARDING_CHANGES_REQUESTED':
      case 'COMPANY_ONBOARDING_SUBMITTED':
      case 'COMPANY_ONBOARDING_RESUBMITTED':
        return 'warning'

      case 'PERMIT_REJECTED':
      case 'CHECKLIST_REJECTED':
      case 'PERMIT_EXTENSION_REJECTED':
      case 'PERMIT_EXTENSION_CANCELLED':
      case 'PERMIT_TRANSFER_REJECTED':
      case 'PERMIT_TRANSFER_DECLINED':
      case 'PERMIT_TRANSFER_CANCELLED':
      case 'PARTICIPANT_REMOVED':
      case 'BRIEFING_DECLINED':
      case 'PERMIT_CANCELLED':
      case 'PERMIT_SUSPENDED':
      case 'PERMIT_SUSPENSION_RESUME_REQUEST_DECLINED':
      case 'PERMIT_SHORTENED':
      case 'PERMIT_DISCARDED':
      case 'PERMIT_EXPIRED':
      case 'PERMIT_FORCE_CHECKED_OUT':
      case 'TASK_CANCELLED':
      case 'USER_REMOVED_FROM_PROJECT_AUTH_GROUP':
      case 'AUTH_GROUP_DELETED':
      case 'PERMIT_DRAFT_DELETED':
      case 'CHECKLIST_DRAFT_DELETED':
      case 'PROJECT_TEMPLATE_REMOVED':
      case 'TEMPLATE_VERSION_DELETED':
      case 'CHECKLIST_TEMPLATE_VERSION_DISCARDED':
      case 'PROJECT_CHECKLIST_TEMPLATE_REMOVED':
      case 'ARCHIVED':
      case 'INSPECTION_EXPIRED':
      case 'ORGANISATION_COMPANY_DELETED':
      case 'COMPANY_ONBOARDING_REJECTED':
      case 'COMPANY_COMPLIANCE_DOCUMENT_REJECTED':
      case 'COMPANY_COMPLIANCE_DOCUMENT_EXPIRED':
      case 'COMPANY_COMPLIANCE_UNASSIGNED':
      case 'USER_COMPETENCY_EXPIRED':
        return 'error'

      default:
        return 'primary'
    }
  }

  public hasAnyAuditActions(actions: Array<AuditAction>): boolean {
    return actions.some((action) => this.action === action)
  }

  /**
   * The approval-stage label for an approval audit (e.g. "Approval Two"),
   * rendered as the `— <label>` postfix on the event. Permits and checklists
   * carry it under their own metadata key.
   */
  public getApprovalLabel(): string | undefined {
    return (
      this.metadata?.permitApprovalLabel ??
      this.metadata?.checklistApprovalLabel
    )
  }

  public isComplianceDetailEvent(): boolean {
    return this.hasAnyAuditActions([
      'COMPANY_COMPLIANCE_DOCUMENT_UPLOADED',
      'COMPANY_COMPLIANCE_DOCUMENT_APPROVED',
      'COMPANY_COMPLIANCE_DOCUMENT_REJECTED',
      'COMPANY_COMPLIANCE_DOCUMENT_EXPIRED',
      'COMPANY_COMPLIANCE_UNASSIGNED',
    ])
  }

  public isComplianceApproved(): boolean {
    return this.isAction('COMPANY_COMPLIANCE_DOCUMENT_APPROVED')
  }

  public isComplianceExpired(): boolean {
    return this.isAction('COMPANY_COMPLIANCE_DOCUMENT_EXPIRED')
  }

  public get complianceTitle(): string | null {
    return this.metadata?.complianceTitle ?? null
  }

  public get complianceRejectionReason(): string | null {
    return this.metadata?.complianceRejectionReason ?? null
  }

  public get complianceExpiryDate(): string | null {
    return this.metadata?.complianceExpiryDate ?? null
  }

  public get firstAttachmentDownloadUrl(): string | null {
    return this.metadata?.attachments?.[0]?.downloadUrl ?? null
  }

  getRegisterItemPropertyValues(
    registerItemProperty: RegisterItemWithLinksPropertyData,
  ): string {
    switch (registerItemProperty.type) {
      case 'STRING':
        return registerItemProperty?.textValue ?? ''
      case 'SELECT':
        return registerItemProperty?.selectedOption?.label ?? ''
      case 'LINK':
        return registerItemProperty?.parentRegisterItem?.uniqueId ?? ''
      case 'DATE':
        const dateRange = registerItemProperty?.dateValue
          ? moment.utc(registerItemProperty?.dateValue).format('D MMM YYYY')
          : ''
        const startDate = registerItemProperty?.startTime
          ? moment(registerItemProperty?.startTime).format('Do MMM yyyy')
          : ''
        const endDate = registerItemProperty?.endTime
          ? moment(registerItemProperty?.endTime).format('Do MMM yyyy')
          : ''
        return dateRange || startDate || endDate
      case 'NUMBER':
        return String(registerItemProperty?.numberValue ?? '')
      case 'USER':
        const user = registerItemProperty?.selectedUser
        return user ? `${user.firstName} ${user.lastName}` : ''
      case 'DIAGRAM':
        return registerItemProperty?.siteBoxDocumentName ?? ''
      default:
        return ''
    }
  }

  public getRegisterItemPropertyColor(
    registerItemProperty: RegisterItemWithLinksPropertyData,
  ): string {
    if (registerItemProperty.type === 'SELECT') {
      return registerItemProperty.selectedOption?.colour
    } else {
      return null
    }
  }

  public getCreatedRegisterItemProperties(): Array<{
    name: string
    value: string
    valueColor?: string
    preValue?: string
    preValueColor?: string
  }> {
    switch (this.action) {
      case 'CREATED':
      case 'REGISTER_ITEM_CREATED':
        return this.metadata.delta?.itemValues
          .map(
            (item: {
              postValue: RegisterItemWithLinksPropertyData
              preValue?: RegisterItemWithLinksPropertyData
            }) => ({
              name: item.postValue.name,
              value: this.getRegisterItemPropertyValues(item.postValue),
              valueColor: this.getRegisterItemPropertyColor(item.postValue),
            }),
          )
          .filter((item) => !!item.value)

      case 'UPDATED':
      case 'REGISTER_ITEM_UPDATED':
      case 'FORMULAS_RECALCULATED':
        return this.metadata.delta?.itemValues.map(
          (item: {
            postValue: RegisterItemWithLinksPropertyData
            preValue?: RegisterItemWithLinksPropertyData
          }) => ({
            name: item.postValue.name,
            value: this.getRegisterItemPropertyValues(item.postValue),
            valueColor: this.getRegisterItemPropertyColor(item.postValue),
            preValue: this.getRegisterItemPropertyValues(item.preValue),
            preValueColor: this.getRegisterItemPropertyColor(item.preValue),
          }),
        )
      case 'REGISTER_ITEM_MOVED_TO_DIAGRAM':
        return [
          {
            name: localizedStrings.diagram,
            value: this.metadata.movedToAnnotationSet.diagramName,
            preValue: this.metadata.movedFromAnnotationSet.diagramName,
          },
        ]
      default:
        return null
    }
  }

  public isMovingRegisterItemsToDiagram(): boolean {
    return !!this.metadata?.movedFromAnnotationSet
  }

  public getCoordinates(): Feature<Point> {
    if (!(this.geoJSON && this.geoJSON.type === 'FeatureCollection')) return
    const { geometry, ...feature } = this.geoJSON.features[0]
    if (geometry.type !== 'Point') return
    return { geometry, ...feature }
  }

  public getAuditPointAccuracyRadius(): Feature<Polygon> {
    if (!(this.geoJSON && this.geoJSON.type === 'FeatureCollection')) return
    const w3wFeaturePoint = this.geoJSON.features.find((feature) => {
      return feature.properties.inputType === 'WHAT_3_WORDS'
    }) as Feature<Point>

    const turfCircle =
      w3wFeaturePoint?.properties?.locationAccuracyInMeters &&
      circle(
        w3wFeaturePoint,
        w3wFeaturePoint?.properties?.locationAccuracyInMeters,
        {
          steps: 50,
          units: 'meters' as Units,
        },
      )

    return turfCircle
  }

  public getPermitCoordinates(): Feature<Point> {
    const { permitGeoJson } = this.metadata
    if (!(permitGeoJson && permitGeoJson.type === 'FeatureCollection')) return
    const { geometry, ...feature } = permitGeoJson.features[0]
    if (geometry.type !== 'Point') return
    return { geometry, ...feature }
  }

  public getInfoTitle(): string {
    switch (this.action) {
      case 'PERMIT_DISCARDED':
        return localizedStrings.automatedInformation
      case 'PERMIT_CHECKLIST_DONE':
        return localizedStrings.checklistInformation
      case 'COMPANY_ONBOARDING_CHANGES_REQUESTED':
        return localizedStrings.comment
      case 'COMPANY_ONBOARDING_REJECTED':
        return localizedStrings.reason
      default:
        return localizedStrings.info
    }
  }

  public isUpdatingGroupPermissions(): boolean {
    return (
      !!this.metadata?.permissionsUpdatedData?.oldPermissions ||
      !!this.metadata?.permissionsUpdatedData?.newPermissions
    )
  }

  public hasWarnings(): boolean {
    return !!this.metadata?.warnings?.length
  }

  public isExtendingPermit(): boolean {
    return !!(
      this.metadata?.permitExtensionEndsOn &&
      this.metadata?.permitPreviousEndsOn
    )
  }

  public isShorteningPermit(): boolean {
    return !!(
      this.metadata?.permitShortenedEndsOn &&
      this.metadata?.permitPreviousEndsOn
    )
  }

  public isChangingPermitHolder(): boolean {
    return !!(
      this.metadata?.changedPermitHolderFrom &&
      this.metadata?.changedPermitHolderTo
    )
  }

  public isNominatingApprovers(): boolean {
    return !!(
      this.metadata?.nominatedApprovers && this.metadata?.nominatedForApproval
    )
  }

  public isTransferring(): boolean {
    return !!(this.metadata?.transferReceiver && this.user)
  }

  public hasComment(): boolean {
    return !!this.metadata?.commentAnsweredQuestionDataResult
  }

  public hasLocationAccuracy(): boolean {
    const featureCollection = this?.geoJSON as FeatureCollection
    return featureCollection?.features?.[0]?.properties
      ?.locationAccuracyInMeters
  }

  public locationAccuracyText(): string {
    const featureCollection = this?.geoJSON as FeatureCollection
    return `${Number(
      featureCollection.features?.[0]?.properties?.locationAccuracyInMeters,
    ).toFixed(2)}m (${
      featureCollection.features?.[0]?.properties?.locationAccuracyStatus
    })`
  }

  public hasPermitStatusChange(): boolean {
    return !!(
      this.metadata?.permitStatusBefore &&
      this.metadata?.permitStatusAfter &&
      this.metadata?.permitStatusBefore !== this.metadata?.permitStatusAfter
    )
  }

  public hasProjectStatusChange(): boolean {
    return !!(
      this.metadata?.projectStatusBefore && this.metadata?.projectStatusAfter
    )
  }

  public hasProofs(): boolean {
    return this.metadata?.attachments?.length > 0
  }

  public hasTemplateInformation(): boolean {
    return !!(
      this.permitTemplate ||
      this.permitTemplateVersion ||
      this.checklistTemplate ||
      this.checklistTemplateVersion
    )
  }

  public isUpdatingProjectMemberRole(): boolean {
    return !!(
      this.metadata?.preProjectMemberData &&
      this.metadata?.postProjectMemberData
    )
  }

  public isUpdatingOrganisationMemberRole(): boolean {
    return !!(
      this.metadata?.preOrgMemberData && this.metadata?.postOrgMemberData
    )
  }

  public isUpdatingTemplatePermissionGroups(): boolean {
    return !!this.metadata?.permissionAssignmentReferenceObjectId
  }

  public isUpdatingRegisterItem(): boolean {
    return !!this.getCreatedRegisterItemProperties()?.length
  }

  public hasInspection(): boolean {
    return !!this.metadata?.inspectionChecklist
  }

  public isCheckInAction(): boolean {
    return (
      this.action === 'PERMIT_CHECKED_IN' ||
      this.action === 'PERMIT_CHECKED_OUT' ||
      this.action === 'PERMIT_CHECK_IN_EXTENDED' ||
      this.action === 'PERMIT_FORCE_CHECKED_OUT'
    )
  }

  public getCheckInSignatureUrls(): string[] {
    const rows =
      this.action === 'PERMIT_CHECKED_IN'
        ? this.metadata?.checkIns
        : this.metadata?.checkOuts
    const attachments = this.metadata?.attachments ?? []
    if (!rows?.length || attachments.length !== rows.length) return []
    return attachments.map(({ getUrl }) => getUrl)
  }

  public getFormattedCheckInExpectedCheckOutOn(): string | undefined {
    return this.metadata?.checkIns?.[0]?.getFormattedExpectedCheckOutOn()
  }

  public isBriefingSession(): boolean {
    return this.action === 'BRIEFING_SESSION_DONE'
  }

  // Emitted when participants are added to a permit whose template has
  // `requiresBriefingSignature: false`, i.e. they are added without being
  // briefed.
  public isAddParticipantsAction(): boolean {
    return this.action === 'BRIEFING_SESSION_NO_SIGN_PARTICIPANTS_ADDED'
  }

  public isBriefingParticipant(): boolean {
    /**
     * NOTE: `PARTICIPANT_BRIEFED` is now only used for self-briefing (e.g. via QR code).
     * In this case, 'selfBriefed' is true in the audit metadata.
     * And other briefings are handled via `BRIEFING_SESSION_DONE` action.
     *
     * However, keep in mind that it was previously used for simple briefings.
     */
    return [
      'PARTICIPANT_BRIEFED',
      'BRIEFING_ACCEPTED',
      'BRIEFING_DECLINED',
    ].includes(this.action)
  }

  public showAppliedToUserBlock(): boolean {
    return (
      this.appliedToUser &&
      ![
        'PARTICIPANT_BRIEFED',
        'PARTICIPANT_REMOVED',
        'BRIEFING_ACCEPTED',
        'BRIEFING_DECLINED',
      ].includes(this.action)
    )
  }

  public isUpdatingSiteBoxDocumentStatus(): boolean {
    return (
      !!this.metadata?.preSiteBoxDocumentValuesData &&
      !!this.metadata?.postSiteBoxDocumentValuesData &&
      this.metadata?.preSiteBoxDocumentValuesData.status !==
        this.metadata?.postSiteBoxDocumentValuesData.status
    )
  }

  public isRenamingSiteBoxDocument(): boolean {
    return (
      !!this.metadata?.preSiteBoxDocumentValuesData &&
      !!this.metadata?.postSiteBoxDocumentValuesData &&
      this.metadata?.preSiteBoxDocumentValuesData.name !==
        this.metadata?.postSiteBoxDocumentValuesData.name
    )
  }

  public isUpdatingProjectDetails(): boolean {
    return (
      !!this.metadata?.projectDataAfter && !!this.metadata?.projectDataBefore
    )
  }

  public hasTaskAddressRequestedChanges(): boolean {
    return this?.metadata?.taskAddressedRequestedChanges
  }

  public isBriefingAction(): boolean {
    return (
      this.isBriefingSession() ||
      this.isBriefingParticipant() ||
      this.isAddParticipantsAction()
    )
  }

  public isDraftAction(): boolean {
    return (
      this.action === 'PERMIT_DRAFT_CREATED' ||
      this.action === 'PERMIT_DRAFT_DELETED' ||
      this.action === 'CHECKLIST_DRAFT_CREATED' ||
      this.action === 'CHECKLIST_DRAFT_DELETED'
    )
  }

  public isTaskAction(): boolean {
    return (
      this.action === 'TASK_CREATED' ||
      this.action === 'TASK_WORK_STEP_SUBMITTED' ||
      this.action === 'TASK_APPROVED' ||
      this.action === 'TASK_UPDATED' ||
      this.action === 'TASK_REASSIGNED' ||
      this.action === 'TASK_CANCELLED' ||
      this.action === 'TASK_REOPENED' ||
      this.action === 'TASK_CHANGES_REQUESTED'
    )
  }

  public isLikedToPermit(): boolean {
    return !!this.metadata?.linkedToPermit
  }

  public isUpdatingOrganisationFeatureFlags(): boolean {
    return !!this.metadata?.preFeatureFlags && !!this.metadata?.postFeatureFlags
  }
}
